import IconButton from '@material-ui/core/IconButton';
import classNames from 'classnames';
import { ReactElement, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import hamburgerIcon from '~/ui/assets/images/hamburger.svg';
import UnreadCounter from '~/ui/components/common/UnreadCounter';
import getChannelDataItem from '~/ui/pages/Coordinate/helpers/getChannelDataItem';
import useChannels from '~/ui/pages/Coordinate/hooks/useChannels';
import useGetCrisisCalls from '~/ui/pages/Network/hooks/useGetCrisisCalls';

import { useStoreActions, useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import sidebarMenuItems from '~/ui/constants/sidebarMenuItems';
import useFeature from '~/store/clinic/hooks/useFeature';

import styles from './Menu.module.scss';

interface IProps {
  url: string;
  title: string;
  icon: any;
  pathname: string;
  isMenuOpened: boolean;
  unreadMessagesCount?: number;
  crisisCallsCount?: number;
}

const Item = ({
  url,
  title,
  icon: Icon,
  pathname,
  isMenuOpened,
  unreadMessagesCount,
  crisisCallsCount,
}: IProps) => {
  const [hover, setHover] = useState(false);
  const isActive = pathname.startsWith(url);

  const innerContent = () => (
    <div className={styles.innerContent}>
      {isMenuOpened && <div className={styles.spacer} />}
      <Icon className={styles.icon} isActive={isActive || hover} />
      {isMenuOpened && <span className={styles.title}>{title}</span>}
    </div>
  );

  return (
    <li
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={styles.menuItem}
    >
      {isActive ? (
        <div className={classNames(styles.link, { [styles.collapsedLink]: !isMenuOpened })}>
          {innerContent()}
        </div>
      ) : (
        <NavLink
          to={url}
          className={classNames(styles.link, { [styles.collapsedLink]: !isMenuOpened })}
          title={title}
        >
          {innerContent()}
        </NavLink>
      )}

      {!!unreadMessagesCount && (
        <div
          className={classNames(styles.unreadCounter, {
            [styles.unreadCounterCollapsed]: !isMenuOpened,
          })}
        >
          <UnreadCounter count={unreadMessagesCount} noBorder className={styles.unreadMessages} />
        </div>
      )}

      {!!crisisCallsCount && (
        <div
          className={classNames(styles.unreadCounter, {
            [styles.unreadCounterCollapsed]: !isMenuOpened,
          })}
        >
          <UnreadCounter count={crisisCallsCount} noBorder className={styles.unreadMessages} />
        </div>
      )}
    </li>
  );
};

const Menu = (): ReactElement => {
  const isMenuOpened = useStoreState(state => state.app.sidebarMenuOpened);
  const sendBirdUserId = useStoreState(state => state.user.current.sendBirdUserId);
  const { isLocalAdmin } = useRole();
  const { current: currentUser } = useStoreState(state => state.user);

  const { isCrisisAvailable } = useFeature();

  useChannels();

  const channels = useStoreState(state => state.coordinate.channels);

  const unreadMessagesCount = useMemo(
    () =>
      channels.reduce((total, obj) => {
        const archivedBy = getChannelDataItem(obj?.data, 'archivedBy') || [];
        const isArchived = sendBirdUserId && archivedBy.includes(sendBirdUserId);
        if (!isArchived) {
          return total + obj.unreadMessageCount;
        }
        return total;
      }, 0),
    [channels, sendBirdUserId],
  );

  const SHOW_CRISIS_CALLS_COUNT = isCrisisAvailable && isLocalAdmin && currentUser?.crisisAccess;

  const { crisisCalls } = useGetCrisisCalls({
    enabled: SHOW_CRISIS_CALLS_COUNT,
  });

  const { showSidebar, hideSidebar } = useStoreActions(actions => actions.app);
  const { pathname } = useLocation();

  const toggleMenu = () => {
    if (isMenuOpened) {
      hideSidebar();
    } else {
      showSidebar();
    }
  };

  return (
    <>
      <section
        className={classNames(styles.menuButton, { [styles.menuButtonClosed]: !isMenuOpened })}
      >
        <IconButton onClick={toggleMenu}>
          <img src={hamburgerIcon} alt="menu" />
        </IconButton>
      </section>
      <ul className={classNames(styles.menu, { [styles.closed]: !isMenuOpened })}>
        {sidebarMenuItems().map(({ url, title, icon: Icon }) => (
          <Item
            key={url}
            url={url}
            title={title}
            icon={Icon}
            pathname={pathname}
            isMenuOpened={isMenuOpened}
            unreadMessagesCount={title === 'Coordinate' ? unreadMessagesCount : undefined}
            crisisCallsCount={
              title === 'Network' && SHOW_CRISIS_CALLS_COUNT ? crisisCalls?.length : undefined
            }
          />
        ))}
      </ul>
    </>
  );
};
export default Menu;
